import React, { useState, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Search, Filter, Calendar, FileText } from 'lucide-react';

export function LogsAdmin({ logs, propriedades = [], usersByEmail = {} }) {
    const [userFilter, setUserFilter] = useState('');
    const [acaoFilter, setAcaoFilter] = useState('');
    const [inicio, setInicio] = useState('');
    const [fim, setFim] = useState('');
    const [propFilter, setPropFilter] = useState('todos');
    const [detOpen, setDetOpen] = useState(false);
    const [selLog, setSelLog] = useState(null);

    const propsById = useMemo(() => {
        const m = {};
        (propriedades || []).forEach(p => { m[String(p.id)] = p; });
        return m;
    }, [propriedades]);

    const filtered = useMemo(() => {
        return (logs || []).filter(r => {
            if (userFilter && !String(r.user_email || '').toLowerCase().includes(userFilter.toLowerCase())) return false;
            if (acaoFilter && !String(r.acao || '').toLowerCase().includes(acaoFilter.toLowerCase())) return false;
            if (propFilter !== 'todos' && String(r.propriedade_id || '') !== String(propFilter)) return false;
            if (inicio) {
                const di = new Date(inicio);
                const d = r.created_at ? new Date(r.created_at) : null;
                if (d && d < di) return false;
            }
            if (fim) {
                const df = new Date(fim);
                const d = r.created_at ? new Date(r.created_at) : null;
                if (d && d > df) return false;
            }
            return true;
        });
    }, [logs, userFilter, acaoFilter, inicio, fim, propFilter]);

    const openDetalhe = (log) => { setSelLog(log); setDetOpen(true); };

    return (
        <div className="space-y-4">
            <div className="bg-stone-50 p-4 rounded-lg border flex flex-col md:flex-row gap-3 items-end md:items-center flex-wrap">
                <div className="flex-1 min-w-[200px] flex flex-col gap-1 w-full">
                    <Label className="text-xs text-stone-500">Usuário</Label>
                    <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-stone-400" />
                        <Input placeholder="Buscar por email" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="pl-8 bg-white" />
                    </div>
                </div>
                <div className="flex-1 min-w-[200px] flex flex-col gap-1 w-full">
                    <Label className="text-xs text-stone-500">Ação</Label>
                    <Input placeholder="Filtrar ação (ex: login)" value={acaoFilter} onChange={(e) => setAcaoFilter(e.target.value)} className="bg-white" />
                </div>
                <div className="flex-1 min-w-[200px] flex flex-col gap-1 w-full">
                    <Label className="text-xs text-stone-500">Propriedade</Label>
                    <Select value={propFilter} onValueChange={setPropFilter}>
                        <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="todos">Todas as propriedades</SelectItem>
                            {(propriedades || []).map(p => (
                                <SelectItem key={String(p.id)} value={String(p.id)}>{p.nome || String(p.id || '')}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-2">
                    <div className="flex flex-col gap-1">
                        <Label className="text-xs text-stone-500">Início</Label>
                        <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className="w-36 bg-white" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Label className="text-xs text-stone-500">Fim</Label>
                        <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className="w-36 bg-white" />
                    </div>
                </div>
            </div>

            <div className="rounded-md border">
                <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-600 font-medium">
                        <tr>
                            <th className="text-left py-3 px-4">Data/Hora</th>
                            <th className="text-left py-3 px-4">Usuário</th>
                            <th className="text-left py-3 px-4">Propriedade</th>
                            <th className="text-left py-3 px-4">Ação</th>
                            <th className="text-left py-3 px-4">Tabela</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Detalhes</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {filtered.length === 0 ? (
                            <tr><td colSpan={7} className="py-8 text-center text-stone-500">Nenhum log encontrado para os filtros selecionados.</td></tr>
                        ) : filtered.map((r, idx) => (
                            <tr key={idx} className="hover:bg-stone-50">
                                <td className="py-3 px-4 whitespace-nowrap text-stone-600">{r.created_at ? format(new Date(r.created_at), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                <td className="py-3 px-4">
                                    <div className="flex flex-col">
                                        <span className="font-medium text-stone-900">{(() => { const u = usersByEmail[String(r.user_email || '')]; return u?.full_name || '-'; })()}</span>
                                        <span className="text-xs text-stone-500">{r.user_email || '-'}</span>
                                    </div>
                                </td>
                                <td className="py-3 px-4 max-w-[200px] truncate" title={(() => { const p = propsById[String(r.propriedade_id || '')]; return p?.nome || String(r.propriedade_id || ''); })()}>
                                    {(() => { const p = propsById[String(r.propriedade_id || '')]; return p?.nome || String(r.propriedade_id || '-'); })()}
                                </td>
                                <td className="py-3 px-4"><Badge variant="outline" className="font-normal">{r.acao || '-'}</Badge></td>
                                <td className="py-3 px-4 text-xs font-mono text-stone-500 bg-stone-100 rounded px-1 py-0.5 w-fit">{r.table || '-'}</td>
                                <td className="py-3 px-4">
                                    <Badge className={r.status === 'success' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : r.status === 'denied' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-stone-100 text-stone-600 border-stone-200'}>
                                        {r.status || '-'}
                                    </Badge>
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <Button variant="ghost" size="sm" onClick={() => openDetalhe(r)} className="h-8 w-8 p-0">
                                        <FileText className="w-4 h-4 text-stone-500" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={detOpen} onOpenChange={setDetOpen}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Detalhes do Log de Auditoria</DialogTitle>
                        <DialogDescription>ID da Auditoria: {selLog?.id}</DialogDescription>
                    </DialogHeader>
                    {!selLog ? (
                        <div className="text-sm text-stone-500">Selecione um log para visualizar</div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 text-sm bg-stone-50 p-4 rounded-md border">
                            <div>
                                <span className="text-stone-500 block mb-1">Data</span>
                                <span className="font-medium">{selLog.created_at ? format(new Date(selLog.created_at), 'dd/MM/yyyy HH:mm:ss') : '-'}</span>
                            </div>
                            <div>
                                <span className="text-stone-500 block mb-1">Usuário</span>
                                <span className="font-medium">{selLog.user_email || '-'}</span>
                            </div>
                            <div>
                                <span className="text-stone-500 block mb-1">Propriedade</span>
                                <span className="font-medium">{(() => { const p = propsById[String(selLog.propriedade_id || '')]; return (p?.nome || String(selLog.propriedade_id || '-')); })()}</span>
                            </div>
                            <div>
                                <span className="text-stone-500 block mb-1">Ação</span>
                                <span className="font-medium">{selLog.acao || '-'}</span>
                            </div>
                            <div>
                                <span className="text-stone-500 block mb-1">Tabela Alvo</span>
                                <span className="font-medium font-mono text-xs">{selLog.table || '-'}</span>
                            </div>
                            <div>
                                <span className="text-stone-500 block mb-1">Status</span>
                                <span className="font-medium">{selLog.status || '-'}</span>
                            </div>
                            <div className="col-span-2 mt-2">
                                <span className="text-stone-500 block mb-2">Payload / Detalhes JSON</span>
                                <pre className="p-3 bg-stone-900 text-stone-50 rounded-md overflow-x-auto font-mono text-xs leading-relaxed">
                                    {(() => {
                                        try {
                                            const obj = typeof selLog.details === 'string' ? JSON.parse(selLog.details) : (selLog.details || {});
                                            return JSON.stringify(obj, null, 2);
                                        } catch {
                                            return String(selLog.details || '{}');
                                        }
                                    })()}
                                </pre>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
